import { complexCommands, simpleCommands } from "src/data/commands";
import i18n from "src/i18n/config";

import RuleCatalog, { MatchedPolicyRule } from "@dashboard/policies/rules/RuleCatalog";
import { getRuleSummary } from "@dashboard/policies/rules/summary";
import { MdmPolicyCommand } from "@devices/types";

const knownCommandNames = new Set<string>([
    ...complexCommands.map((command) => command.name),
    ...simpleCommands.map((command) => command.template?.command).filter((name): name is string => Boolean(name)),
]);

const knownCommandDisplayNames = new Map<string, string>();
complexCommands.forEach((command) => {
    knownCommandDisplayNames.set(command.name, command.schema.title);
});
simpleCommands.forEach((command) => {
    const commandName = command.template?.command;
    const displayName = command.template?.caption ?? command.schema.title;
    if (commandName && displayName && !knownCommandDisplayNames.has(commandName)) {
        knownCommandDisplayNames.set(commandName, displayName);
    }
});

export type PolicyRuleLabels = {
    /**
     * The rule's own name, worded exactly as the rule picker words it.
     */
    primary: string;
    /**
     * What this rule is configured to do, or the kind of command for one no rule describes.
     */
    secondary: string | null;
    recognized: boolean;
};

const getCommandName = (policyCommand: MdmPolicyCommand): string | null => {
    const command = policyCommand.command;
    return typeof command === "string" && command.trim() ? command : null;
};

const getCaption = (policyCommand: MdmPolicyCommand): string | null => {
    const caption = policyCommand.caption;
    return typeof caption === "string" && caption.trim() ? caption : null;
};

export function getPolicyRuleLabels(
    policyCommand: MdmPolicyCommand,
    matchedRule: MatchedPolicyRule | null = RuleCatalog.fromCommand(policyCommand),
): PolicyRuleLabels {
    const commandName = getCommandName(policyCommand);
    const caption = getCaption(policyCommand);

    if (!commandName) {
        return {
            primary: i18n.t("modals:importPolicy.preview.invalidEntry"),
            secondary: null,
            recognized: false,
        };
    }

    if (matchedRule) {
        return {
            primary: matchedRule.rule.title(),
            secondary: getRuleSummary(matchedRule.rule, matchedRule.values),
            recognized: true,
        };
    }

    return {
        primary: caption ?? knownCommandDisplayNames.get(commandName) ?? commandName,
        secondary: i18n.t("modals:editPolicyRule.customRule"),
        recognized: knownCommandNames.has(commandName),
    };
}
